import type * as yup from 'yup';

/**
 * Create a type in which specific fields of the resource are required. Useful when using with `yup`
 * for ensuring a type is validated to have certain fields
 * @example
 * type Person = { name?: string; email?: string; age?: string }
 * type RequiredField = 'name' | 'email'
 *
 * type ValidatedPerson = Validated<Person, RequiredField>
 * // type ValidatedPerson = { name: string; email: string; age?: string }
 */
export type Validated<TResource, TKey extends keyof TResource> = Omit<
  TResource,
  TKey
> &
  Required<Pick<TResource, TKey>>;

/**
 * Useful for somewhat enforcing type correctness on top-level fields when defining yup schemas
 *
 * Ensures the correctness of field names but does not validate their cast/validated type
 *
 * @example
 * type MyObject = {
 *   thisField: string;
 * };
 *
 * export const invalidSchema = yup.object<ObjectType<MyObject>>({
 *   // will throw a ts error here
 *   thisFieldIsIncorrect: yup.string(),
 * });
 *
 * export const validSchema = yup.object<ObjectType<MyObject>>({
 *   // will not throw a ts error here
 *   thisField: yup.string(),
 * });
 */
export type ObjectType<TObject> = Partial<Record<keyof TObject, yup.AnySchema>>;
